import * as z$1 from "zod/v4";
//#region src/schemas/protocol/space-attachment.ts
const SPACE_ATTACHMENT_ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];
const JPEG_MAGIC_BYTES = [
	255,
	216,
	255
];
const PNG_MAGIC_BYTES = [
	137,
	80,
	78,
	71,
	13,
	10,
	26,
	10
];
const SPACE_ATTACHMENT_MAX_SIZE_BYTES = 5242880;
/**
* W-ATT2 owns enforcement when a message starts referencing attachments.
*/
const SPACE_ATTACHMENT_MAX_COUNT_PER_MESSAGE = 4;
const SPACE_ATTACHMENT_MAX_DIMENSION = 2e4;
const SPACE_ATTACHMENT_MAX_FILE_NAME_LENGTH = 255;
const FILE_NAME_PATH_SEPARATOR_PATTERN = /[\\/]/;
const spaceAttachmentScopeIdSchema = z$1.string().trim().min(1).max(512);
const spaceAttachmentIdSchema = z$1.string().trim().min(1).max(128);
const spaceAttachmentMimeTypeSchema = z$1.enum(SPACE_ATTACHMENT_ALLOWED_MIME_TYPES);
const spaceAttachmentSizeBytesSchema = z$1.number().int().finite().min(1).max(SPACE_ATTACHMENT_MAX_SIZE_BYTES);
/**
* Width and height are client-declared layout hints. The server stores them
* without decoding the image and does not verify them against the image bytes.
*/
const spaceAttachmentDimensionSchema = z$1.number().int().finite().min(1).max(SPACE_ATTACHMENT_MAX_DIMENSION);
const spaceAttachmentFileNameSchema = z$1.string().transform((value) => value.trim().split(FILE_NAME_PATH_SEPARATOR_PATTERN).at(-1)?.trim() ?? "").pipe(z$1.string().min(1).max(255));
const spaceAttachmentDescriptorSchema = z$1.strictObject({
	id: spaceAttachmentIdSchema,
	spaceId: spaceAttachmentScopeIdSchema,
	organizationId: spaceAttachmentScopeIdSchema,
	mimeType: spaceAttachmentMimeTypeSchema,
	sizeBytes: spaceAttachmentSizeBytesSchema,
	/** Client-declared layout hint; not verified against the image bytes. */
	width: spaceAttachmentDimensionSchema.optional(),
	/** Client-declared layout hint; not verified against the image bytes. */
	height: spaceAttachmentDimensionSchema.optional(),
	fileName: spaceAttachmentFileNameSchema.optional(),
	createdAt: z$1.iso.datetime({ offset: true })
});
/**
* Attachment metadata embedded in a Space message. Scope and creation facts
* stay in Core-owned attachment metadata and are not repeated in the signal.
*/
const spaceMessageAttachmentSchema = z$1.strictObject({
	id: spaceAttachmentIdSchema,
	mimeType: spaceAttachmentMimeTypeSchema,
	sizeBytes: spaceAttachmentSizeBytesSchema,
	width: spaceAttachmentDimensionSchema.optional(),
	height: spaceAttachmentDimensionSchema.optional(),
	fileName: spaceAttachmentFileNameSchema.optional()
});
const spaceMessageAttachmentsSchema = z$1.array(spaceMessageAttachmentSchema).max(4);
function detectSpaceAttachmentMimeType(bytes) {
	const actual = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	if (matchesMagicBytes(actual, PNG_MAGIC_BYTES)) return "image/png";
	if (matchesMagicBytes(actual, JPEG_MAGIC_BYTES)) return "image/jpeg";
	return null;
}
function projectSpaceMessageAttachment(descriptor) {
	return spaceMessageAttachmentSchema.parse({
		id: descriptor.id,
		mimeType: descriptor.mimeType,
		sizeBytes: descriptor.sizeBytes,
		...descriptor.width === void 0 ? {} : { width: descriptor.width },
		...descriptor.height === void 0 ? {} : { height: descriptor.height },
		...descriptor.fileName === void 0 ? {} : { fileName: descriptor.fileName }
	});
}
function buildSpaceAttachmentObjectKey(input) {
	return `org/${input.organizationId}/space/${input.spaceId}/${input.id}`;
}
function matchesMagicBytes(bytes, expected) {
	return expected.every((value, index) => bytes[index] === value);
}
/** Core-owned metadata payload carried inside the authenticated RPC wrapper. */
const recordSpaceAttachmentPayloadSchema = z$1.strictObject({
	spaceId: spaceAttachmentScopeIdSchema,
	mimeType: spaceAttachmentMimeTypeSchema,
	sizeBytes: spaceAttachmentSizeBytesSchema,
	width: spaceAttachmentDimensionSchema.optional(),
	height: spaceAttachmentDimensionSchema.optional(),
	fileName: spaceAttachmentFileNameSchema.optional()
});
const deleteSpaceAttachmentRecordInputSchema = z$1.strictObject({ attachmentId: spaceAttachmentIdSchema });
const deleteSpaceAttachmentRecordResultSchema = z$1.strictObject({ deleted: z$1.boolean() });
const resolveSpaceAttachmentForDownloadPayloadSchema = z$1.strictObject({
	spaceId: spaceAttachmentScopeIdSchema,
	attachmentId: spaceAttachmentIdSchema
});
//#endregion
export { SPACE_ATTACHMENT_ALLOWED_MIME_TYPES, SPACE_ATTACHMENT_MAX_COUNT_PER_MESSAGE, SPACE_ATTACHMENT_MAX_DIMENSION, SPACE_ATTACHMENT_MAX_FILE_NAME_LENGTH, SPACE_ATTACHMENT_MAX_SIZE_BYTES, buildSpaceAttachmentObjectKey, deleteSpaceAttachmentRecordInputSchema, deleteSpaceAttachmentRecordResultSchema, detectSpaceAttachmentMimeType, projectSpaceMessageAttachment, recordSpaceAttachmentPayloadSchema, resolveSpaceAttachmentForDownloadPayloadSchema, spaceAttachmentDescriptorSchema, spaceAttachmentDimensionSchema, spaceAttachmentFileNameSchema, spaceAttachmentIdSchema, spaceAttachmentMimeTypeSchema, spaceAttachmentSizeBytesSchema, spaceMessageAttachmentSchema, spaceMessageAttachmentsSchema };

//# sourceMappingURL=space-attachment.js.map